import { useEffect, useMemo, useRef, useState } from "react";
import {
  createBatch,
  finalizeBatch,
  getBatch,
  uploadFileXHR,
} from "./lib/api.js";
import { sha256Hex } from "./lib/sha256.js";
import {
  SOURCE_DOMAIN_FR,
  detectSourceDomain,
} from "./lib/sourceDomain.js";

const MAX_FILE_SIZE = 30 * 1024 * 1024;
const CONCURRENCY = 3;
const LS_KEY = "vivatax_upload_batch";
const LOGO_URL_CH =
  "https://cdn.prod.website-files.com/668c16e02d905d343797cc16/66a7c289e13debf13e4692dd_Frame%2078.svg";
const LOGO_URL_FR =
  "https://cdn.prod.website-files.com/6a4ba2282137ee54066570a6/6a4d1335429831cad4235226_logo.svg";

type Status = "idle" | "hashing" | "uploading" | "complete" | "partial_failed";

interface LocalFile {
  id?: string; // server-assigned after batch create
  file: File;
  originalName: string;
  sha256?: string;
  status: "pending" | "uploading" | "success" | "failed";
  progress: number;
  errorMessage?: string | null;
  sizeBytes: number;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getClientMeta() {
  const p = new URLSearchParams(window.location.search);
  const name = (p.get("name") || p.get("clientName") || "").trim();
  const email = (
    p.get("email") || p.get("clientEmail") || p.get("notification") || ""
  ).trim();
  return { clientName: name, clientEmail: email };
}

type Tab = "pending" | "success" | "failed";

export function App() {
  const { clientName, clientEmail } = useMemo(getClientMeta, []);
  const sourceDomain = useMemo(
    () => detectSourceDomain(window.location.hostname),
    [],
  );
  const logoUrl = sourceDomain === SOURCE_DOMAIN_FR ? LOGO_URL_FR : LOGO_URL_CH;
  const missing: string[] = [];
  if (!clientName) missing.push("nom du client");
  if (!clientEmail) missing.push("email du client");
  if (!sourceDomain) missing.push("domaine de téléversement autorisé");

  const [files, setFiles] = useState<LocalFile[]>([]);
  const [status, setStatus] = useState<Status>("idle");
  const [batchId, setBatchId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [folderUrl, setFolderUrl] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string>("");
  const [tab, setTab] = useState<Tab>("pending");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resume: check localStorage on mount
  useEffect(() => {
    if (missing.length) return;
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { batchId: string; token: string };
      void (async () => {
        try {
          const state = await getBatch(parsed.batchId, parsed.token);
          if (
            state.status === "complete" ||
            state.status === "partial_failed"
          ) {
            localStorage.removeItem(LS_KEY);
            return;
          }
          setBatchId(parsed.batchId);
          setToken(parsed.token);
          setFolderUrl(state.folderUrl);
          setStatusMsg(
            `Session précédente détectée — ${state.files.length} fichier(s). Sélectionnez-les à nouveau pour reprendre.`,
          );
        } catch {
          localStorage.removeItem(LS_KEY);
        }
      })();
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }, []);

  function addFiles(list: FileList | File[]) {
    const arr = Array.from(list);
    setFiles((prev) => {
      const existing = new Set(
        prev.map((p) => `${p.file.size}-${p.file.lastModified}-${p.file.name}`),
      );
      const next = [...prev];
      for (const f of arr) {
        const key = `${f.size}-${f.lastModified}-${f.name}`;
        if (existing.has(key)) continue;
        const tooBig = f.size > MAX_FILE_SIZE;
        next.push({
          file: f,
          originalName: f.name,
          sizeBytes: f.size,
          status: tooBig ? "failed" : "pending",
          progress: 0,
          errorMessage: tooBig
            ? `Fichier trop volumineux: ${formatBytes(f.size)} (max 30 MB)`
            : null,
        });
      }
      return next;
    });
  }

  function removeFile(idx: number) {
    if (status === "uploading" || status === "hashing") return;
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function startUpload() {
    if (!clientName || !clientEmail || !sourceDomain) return;
    const uploadable = files.filter((f) => f.status === "pending");
    if (uploadable.length === 0) {
      setStatusMsg("Aucun fichier à télécharger.");
      return;
    }

    setStatus("hashing");
    setStatusMsg("Préparation des fichiers…");

    // Hash all uploadable files
    try {
      const hashed = await Promise.all(
        uploadable.map(async (lf) => ({
          lf,
          sha: await sha256Hex(lf.file),
        })),
      );
      setFiles((prev) =>
        prev.map((p) => {
          const h = hashed.find((x) => x.lf.file === p.file);
          return h ? { ...p, sha256: h.sha } : p;
        }),
      );

      setStatus("uploading");
      setStatusMsg("Création du lot…");

      const createRes = await createBatch({
        clientName,
        clientEmail,
        sourceDomain,
        files: hashed.map(({ lf, sha }) => ({
          name: lf.originalName,
          size: lf.sizeBytes,
          sha256: sha,
          mime: lf.file.type || "",
        })),
      });

      setBatchId(createRes.batchId);
      setToken(createRes.token);
      setFolderUrl(createRes.folderUrl);
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ batchId: createRes.batchId, token: createRes.token }),
      );

      // Map server file IDs onto local state via sha256
      const shaToId = new Map<string, string>();
      for (const srv of createRes.files) {
        const match = hashed.find((h) => h.lf.originalName === srv.originalName);
        if (match) shaToId.set(match.sha, srv.id);
      }

      setFiles((prev) =>
        prev.map((p) => {
          if (!p.sha256) return p;
          const id = shaToId.get(p.sha256);
          return id ? { ...p, id } : p;
        }),
      );

      // Upload with concurrency
      const queue = hashed.map(({ lf, sha }) => ({
        lf,
        sha,
        id: shaToId.get(sha)!,
      }));

      setTab("pending");

      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (queue.length) {
          const item = queue.shift();
          if (!item) break;
          await uploadOne(
            item.lf.file,
            item.id,
            createRes.batchId,
            createRes.token,
          );
        }
      });
      await Promise.all(workers);

      // Finalize
      setStatusMsg("Finalisation…");
      const fin = await finalizeBatch(createRes.batchId, createRes.token);
      localStorage.removeItem(LS_KEY);
      setFolderUrl(fin.folderUrl);

      if (fin.status === "complete") {
        setStatus("complete");
        setStatusMsg("Tous les fichiers ont été téléchargés avec succès !");
        setTab("success");
      } else {
        setStatus("partial_failed");
        setStatusMsg(
          `${fin.succeeded} fichier(s) envoyé(s), ${fin.failed} en échec.`,
        );
        setTab("failed");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatusMsg(`Erreur: ${msg}`);
      setStatus("partial_failed");
    }
  }

  async function uploadOne(
    file: File,
    fileId: string,
    bId: string,
    tok: string,
  ) {
    setFiles((prev) =>
      prev.map((p) =>
        p.file === file ? { ...p, status: "uploading", progress: 0 } : p,
      ),
    );

    const res = await uploadFileXHR({
      batchId: bId,
      fileId,
      token: tok,
      file,
      onProgress: (pct) => {
        setFiles((prev) =>
          prev.map((p) => (p.file === file ? { ...p, progress: pct } : p)),
        );
      },
    });

    if (res.ok) {
      setFiles((prev) =>
        prev.map((p) =>
          p.file === file
            ? { ...p, status: "success", progress: 100, errorMessage: null }
            : p,
        ),
      );
    } else {
      setFiles((prev) =>
        prev.map((p) =>
          p.file === file
            ? {
                ...p,
                status: "failed",
                errorMessage:
                  res.error ||
                  (res.status === 0 ? "Réseau indisponible" : `Erreur ${res.status}`),
              }
            : p,
        ),
      );
    }
  }

  const counts = {
    pending: files.filter(
      (f) => f.status === "pending" || f.status === "uploading",
    ).length,
    success: files.filter((f) => f.status === "success").length,
    failed: files.filter((f) => f.status === "failed").length,
  };
  const visibleFiles = files.filter((f) => {
    if (tab === "pending") return f.status === "pending" || f.status === "uploading";
    if (tab === "success") return f.status === "success";
    return f.status === "failed";
  });

  const busy = status === "hashing" || status === "uploading";

  useEffect(() => {
    if (status === "complete") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const confetti = new (window as any).JSConfetti();
      confetti.addConfetti({ confettiColors: ["#fb673b", "#2e7d32", "#4a90e2", "#ffa726", "#66bb6a"] });
    }
  }, [status]);

  if (missing.length > 0) {
    return (
      <div className="container">
        <a href="https://vivatax.ch">
          <img
            className="logo"
            src={logoUrl}
            alt="Vivatax"
          />
        </a>
        <div className="error-banner">
          Informations manquantes : {missing.join(", ")}.
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <a href="https://vivatax.ch">
        <img
          className="logo"
          src={logoUrl}
          alt="Vivatax"
        />
      </a>

      <p>Veuillez téléverser vos fichiers ici. (Taille maximale&nbsp;: 30 Mo)</p>

      <label
        className={`upload-area ${dragOver ? "dragover" : ""}`}
        htmlFor="fileInput"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (busy) return;
          if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
        }}
      >
        <p>{busy ? "Téléversement en cours…" : "Cliquez ou glissez vos fichiers"}</p>
      </label>
      <input
        id="fileInput"
        type="file"
        multiple
        ref={inputRef}
        disabled={busy}
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="tabs-header">
        <button
          className={`tab-btn ${tab === "pending" ? "active" : ""}`}
          onClick={() => setTab("pending")}
          type="button"
        >
          En cours ({counts.pending})
        </button>
        <button
          className={`tab-btn ${tab === "success" ? "active" : ""}`}
          onClick={() => setTab("success")}
          type="button"
        >
          Téléchargé ({counts.success})
        </button>
        <button
          className={`tab-btn ${tab === "failed" ? "active" : ""}`}
          onClick={() => setTab("failed")}
          type="button"
        >
          Échoué ({counts.failed})
        </button>
      </div>

      <div className="tab-content active">
        <div className="file-list">
          {visibleFiles.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#666" }}>
              Aucun fichier.
            </div>
          )}
          {visibleFiles.map((f) => {
            const realIdx = files.indexOf(f);
            return (
              <div key={realIdx} className={`file-list-item ${f.status}`}>
                {f.status === "success" && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="11" stroke="#66bb6a" strokeWidth="2" fill="#e8f5e9"/>
                    <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#66bb6a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {f.status === "failed" && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="11" stroke="#ef5350" strokeWidth="2" fill="#ffebee"/>
                    <path d="M8 8l8 8M16 8l-8 8" stroke="#ef5350" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                )}
                <div style={{ flex: 1 }}>
                  <div className="file-name">{f.originalName}</div>
                  <div className="file-meta">{formatBytes(f.sizeBytes)}</div>
                  {f.status === "uploading" && (
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${f.progress}%` }}
                      />
                    </div>
                  )}
                  {f.status === "failed" && f.errorMessage && (
                    <div className="file-err">{f.errorMessage}</div>
                  )}
                </div>
                {f.status !== "uploading" && !busy && (
                  <button
                    className="remove-btn"
                    type="button"
                    onClick={() => removeFile(realIdx)}
                  >
                    X
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {status === "complete" && (
        <div className="success-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: "middle", marginRight: 6 }}>
            <circle cx="12" cy="12" r="11" stroke="#2e7d32" strokeWidth="2" fill="#e8f5e9"/>
            <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#2e7d32" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Fichiers envoyés avec succès.
        </div>
      )}
      {status === "partial_failed" && (
        <div className="error-banner">
          Certains fichiers ont échoué. Cliquez à nouveau sur « Envoyer » pour
          réessayer les fichiers en échec.
        </div>
      )}

      <div className="status-msg">{statusMsg}</div>

      <button
        type="button"
        disabled={busy || files.filter((f) => f.status === "pending").length === 0}
        onClick={startUpload}
      >
        {busy ? "Envoi…" : "Envoyer"}
      </button>
    </div>
  );
}
